import unescapedText from 'I18n/utils/unescapedText';
import { LeftIcon, RightIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ArrowButtonWrapper = styled.div.withConfig({
  displayName: "Slider__ArrowButtonWrapper",
  componentId: "sc-1nhuoee-0"
})(["position:absolute;top:40.5%;", " opacity:", ";visibility:", ";transition:opacity 0.3s ease,visibility 0.3s ease;"], ({
  $position
}) => $position === 'left' ? 'left: -19px;' : 'right: -19px;', ({
  $visible
}) => $visible ? '1' : '0', ({
  $visible
}) => $visible ? 'visible' : 'hidden');
const CARD_WIDTH = 360;
const CARD_GAP = '600';
export function Slider({
  children
}) {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const theme = useTheme();
  const updateScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (container) {
      // Check if we can scroll left (not at the start)
      setCanScrollLeft(container.scrollLeft > 0);

      // Check if we can scroll right (not at the end)
      const hasRightScroll = container.scrollLeft + container.clientWidth < container.scrollWidth;
      setCanScrollRight(hasRightScroll);
    }
  };
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // @ts-expect-error remove the scrollbar (for some reason, this property is not typed)
      container.style.scrollbarWidth = 'none';
      updateScrollButtons();
      container.addEventListener('scroll', updateScrollButtons);

      // Initial check and window resize handler
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        container.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, []);
  const getVisibleCardScrollAmount = () => {
    const container = scrollContainerRef.current;
    if (!container) return 0;

    // Calculate width of one card including gap
    const cardWidth = CARD_WIDTH;
    const cardGap = parseInt(theme.space[CARD_GAP], 10);
    const itemWidth = cardWidth + cardGap;

    // Calculate how many cards are visible
    const visibleCards = Math.floor(container.clientWidth / itemWidth);

    // Return the width of visible cards (scroll at least one card)
    return itemWidth * Math.max(1, visibleCards);
  };
  const handleScrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = getVisibleCardScrollAmount() * -1;
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const handleScrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = getVisibleCardScrollAmount();
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  return /*#__PURE__*/_jsxs(View, {
    position: "relative",
    children: [/*#__PURE__*/_jsx(ScrollContainer, {
      scrollElementRef: scrollContainerRef,
      overflowY: "visible",
      scrollDirection: "horizontal",
      children: /*#__PURE__*/_jsx(Flex, {
        gap: CARD_GAP,
        padding: "100",
        children: children.map((child, index) => /*#__PURE__*/_jsx(Flex, {
          inlineSize: `${CARD_WIDTH}px`,
          flexShrink: 0,
          alignItems: "stretch",
          children: child
        }, index))
      })
    }), /*#__PURE__*/_jsx(ArrowButtonWrapper, {
      $position: "left",
      $visible: canScrollLeft,
      children: /*#__PURE__*/_jsx(IconButton, {
        shape: "circle",
        onClick: handleScrollLeft,
        children: /*#__PURE__*/_jsx(LeftIcon, {
          "aria-label": unescapedText('marketplaceStorefront.slider.scrollLeft')
        })
      })
    }), /*#__PURE__*/_jsx(ArrowButtonWrapper, {
      $position: "right",
      $visible: canScrollRight,
      children: /*#__PURE__*/_jsx(IconButton, {
        shape: "circle",
        onClick: handleScrollRight,
        children: /*#__PURE__*/_jsx(RightIcon, {
          "aria-label": unescapedText('marketplaceStorefront.slider.scrollRight')
        })
      })
    })]
  });
}